import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {DatePickerInput as PeacockDatePickerInput} from '@pepsico-ds/ui';
import {Modal} from '@/components/atoms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {downloadDigitalCodeReport} from '@/services/rewardCatalog';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {useAppDispatch} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {convertToIsoString} from '@/utils/commons';
import {logger} from '@/helpers/logger';

const parseYMDToDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const DownloadDigitalCodeReportModal = ({showModal, onClose}) => {
  const dispatch = useAppDispatch();
  const {downloadFromBlobUrl} = useAzureBlob();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleStartDateChange = (formattedDate) => {
    setStartDate(formattedDate || '');
    setStartDateError('');
    setEndDateError('');
    const dateObj = parseYMDToDate(formattedDate);
    const endDateObj = parseYMDToDate(endDate);
    if (endDateObj && dateObj && dateObj > endDateObj) {
      setStartDateError('Start date cannot be after end date');
    }
  };

  const handleEndDateChange = (formattedDate) => {
    setEndDate(formattedDate || '');
    setEndDateError('');
    setStartDateError('');
    const dateObj = parseYMDToDate(formattedDate);
    const startDateObj = parseYMDToDate(startDate);
    if (startDateObj && dateObj && startDateObj > dateObj) {
      setEndDateError('End date cannot be before start date');
    }
  };

  const isDownloadDisabled =
    !startDate ||
    !endDate ||
    !!startDateError ||
    !!endDateError ||
    isDownloading;

  const resetState = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setStartDateError('');
    setEndDateError('');
    setShowCancelConfirm(false);
    setIsDownloading(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const startDateObj = parseYMDToDate(startDate);
      const endDateObj = parseYMDToDate(endDate);
      const response = await downloadDigitalCodeReport({
        startDate: convertToIsoString(startDateObj, true, false),
        endDate: convertToIsoString(endDateObj, false, true)
      });

      if (response?.success && response?.blobUrl && response?.sasToken) {
        const csvText = await downloadFromBlobUrl(
          response.blobUrl,
          response.sasToken
        );
        if (!csvText) throw new Error('Blob download failed');
        const fileName = `DigitalCodeReport_${getDateWithTimeString()}`;
        downloadCSVFiles(csvText, fileName);
        handleCloseModal();
      } else {
        handleCloseModal();
        setTimeout(() => {
          dispatch(
            setToastNotifications({
              show: true,
              message:
                response?.message ||
                'No digital codes found for the specified date range',
              type: 'error'
            })
          );
        }, 300);
      }
    } catch (error) {
      logger.error('Error downloading digital code report:', error);
      handleCloseModal();
      setTimeout(() => {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Failed to download digital code report',
            type: 'error'
          })
        );
      }, 300);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCancelClick = () => {
    if (startDate || endDate) {
      setShowCancelConfirm(true);
    } else {
      handleCloseModal();
    }
  };

  return (
    <>
      <Modal
        type={MODAL_TYPE.FLUID}
        showModal={showModal && !showCancelConfirm}
        isDark
        title='Download Digital Code Report'
        primaryButtonText={isDownloading ? 'Downloading...' : 'Download'}
        secondaryButtonText='Cancel'
        showSecondaryButton
        showPrimaryButton
        primaryButtonDisabled={isDownloadDisabled}
        onCloseModal={handleCancelClick}
        onClickPrimaryButton={handleDownload}
        onClickSecondaryButton={handleCancelClick}
        primaryButtonProps={{
          iconLeading: 'file_download'
        }}
        content={
          <div
            className='flex flex-col gap-4 pt-2'
            style={{overflow: 'visible', minHeight: '320px'}}>
            <p className='text-sm text-gray-300'>
              Select a start and end date to generate and download the report.
              <br />
              The report will include all digital code activity within the
              selected date range.
            </p>
            <div className='grid grid-cols-2 gap-6'>
              <div className='flex flex-col gap-1'>
                <PeacockDatePickerInput
                  label='Start Date*'
                  placeholder='yyyy-mm-dd'
                  selected={startDate || undefined}
                  setSelected={(formattedDate) =>
                    handleStartDateChange(formattedDate)
                  }
                  format='yyyy-mm-dd'
                  error={!!startDateError}
                  helperText={startDateError || ''}
                  feedbackType={startDateError ? 'error' : 'info'}
                  data-cy='start-date'
                />
              </div>
              <div className='flex flex-col gap-1'>
                <PeacockDatePickerInput
                  label='End Date*'
                  placeholder='yyyy-mm-dd'
                  selected={endDate || undefined}
                  setSelected={(formattedDate) =>
                    handleEndDateChange(formattedDate)
                  }
                  format='yyyy-mm-dd'
                  error={!!endDateError}
                  helperText={endDateError || ''}
                  feedbackType={endDateError ? 'error' : 'info'}
                  data-cy='end-date'
                />
              </div>
            </div>
          </div>
        }
      />

      {showCancelConfirm && (
        <Modal
          type={MODAL_TYPE.SMALL}
          showModal={showCancelConfirm}
          isDark
          title='Are you sure you want to cancel?'
          showPrimaryButton
          primaryButtonText='Yes'
          onClickPrimaryButton={handleCloseModal}
          showSecondaryButton
          secondaryButtonText='No'
          onClickSecondaryButton={() => setShowCancelConfirm(false)}
          onCloseModal={() => setShowCancelConfirm(false)}
          footerProps={{
            size: 'medium',
            type: 'primary'
          }}
        />
      )}
    </>
  );
};

DownloadDigitalCodeReportModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default DownloadDigitalCodeReportModal;
